import { useEffect, useRef, useState } from "react";
import {
  ChartNoAxesCombined,
  Check,
  CirclePlus,
  Copy,
  ExternalLink,
  MousePointerClick,
  Settings,
  Trash2,
  UsersRound,
} from "lucide-react";
import duckSearchGif from "../../../assets/utya-search.gif";
import duckWinnerGif from "../../../assets/utya-winner.gif";
import { openTelegramLink } from "../../../lib/tg";
import { driverApi } from "../lib/api";

type Flow = {
  id: number;
  name: string;
  share_url: string;
  status: string;
  resource_category: "required_subscription" | "broadcast" | null;
  today_clicks?: number;
  today_subscribers?: number;
};

type Props = {
  onCreate: () => void;
  onEdit: (id: number) => void;
  onStats: (id: number, name: string) => void;
};

export function FlowsList({ onCreate, onEdit, onStats }: Props) {
  const [items, setItems] = useState<Flow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const deleteConfirmTimer = useRef<number | null>(null);
  const loadGeneration = useRef(0);
  const hasFlows = Boolean(items && items.length > 0);
  const showEmpty = items?.length === 0 && !error;

  async function load() {
    const generation = ++loadGeneration.current;
    setItems(null);
    setError(null);
    try {
      const r = await driverApi.listFlows();
      if (generation !== loadGeneration.current) return;
      if (r.data) {
        setItems(r.data.items as unknown as Flow[]);
      } else {
        setItems([]);
        setError(
          "не удалось загрузить потоки. откройте Mini App из бота заливщика.",
        );
      }
    } catch {
      if (generation === loadGeneration.current) {
        setItems([]);
        setError(
          "не удалось загрузить потоки. откройте Mini App из бота заливщика.",
        );
      }
    }
  }

  useEffect(() => {
    void load();
    return () => {
      loadGeneration.current += 1;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (deleteConfirmTimer.current !== null) {
        window.clearTimeout(deleteConfirmTimer.current);
      }
    };
  }, []);

  async function copy(flow: Flow) {
    try {
      await navigator.clipboard.writeText(flow.share_url);
      setCopiedId(flow.id);
      window.setTimeout(() => setCopiedId(null), 1500);
    } catch {
      setError(
        "копирование недоступно в этом клиенте Telegram. откройте ссылку и скопируйте её из сообщения/адреса.",
      );
    }
  }

  function open(url: string) {
    if (url.startsWith("https://t.me/")) {
      openTelegramLink(url);
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  }

  async function remove(id: number) {
    setDeletingId(id);
    setError(null);
    try {
      const result = await driverApi.deleteFlow(id);
      if (result.error) {
        setError("не удалось удалить поток");
        return;
      }
      setItems((prev) => (prev ?? []).filter((f) => f.id !== id));
    } catch {
      setError("не удалось удалить поток");
    } finally {
      setDeletingId(null);
    }
  }

  function requestRemove(id: number) {
    if (pendingDeleteId === id) {
      setPendingDeleteId(null);
      if (deleteConfirmTimer.current !== null) {
        window.clearTimeout(deleteConfirmTimer.current);
        deleteConfirmTimer.current = null;
      }
      void remove(id);
      return;
    }
    setPendingDeleteId(id);
    if (deleteConfirmTimer.current !== null) {
      window.clearTimeout(deleteConfirmTimer.current);
    }
    deleteConfirmTimer.current = window.setTimeout(() => {
      setPendingDeleteId((current) => (current === id ? null : current));
      deleteConfirmTimer.current = null;
    }, 2400);
  }

  if (items === null && !error) {
    return (
      <div className="tp-screen tp-driver-menu-screen">
        <div className="tp-card-soft text-sm text-muted-foreground">
          загрузка...
        </div>
      </div>
    );
  }

  return (
    <div className="tp-screen tp-driver-menu-screen">
      <div className="tp-hero text-center">
        {hasFlows ? (
          <img
            src={duckWinnerGif}
            alt=""
            aria-hidden="true"
            className="tp-driver-menu-duck"
            draggable={false}
          />
        ) : null}
        <div>
          {!hasFlows ? <h1 className="tp-title">меню</h1> : null}
          <p className="tp-driver-menu-lead">
            создание ссылок, управление показами и настройка блоков
          </p>
        </div>
      </div>

      {hasFlows ? (
        <button
          className="tp-button-primary tp-driver-create-button"
          onClick={onCreate}
        >
          <CirclePlus className="h-5 w-5" aria-hidden="true" />
          <span>создать ссылки</span>
        </button>
      ) : null}

      {error ? (
        <div role="alert" className="tp-alert">
          <div>{error}</div>
          <button
            className="tp-button-secondary mt-3"
            onClick={() => void load()}
          >
            повторить
          </button>
        </div>
      ) : null}

      {showEmpty ? (
        <div className="tp-driver-empty-stage">
          <section className="tp-driver-empty-card" aria-label="ссылок нет">
            <img
              src={duckSearchGif}
              alt=""
              aria-hidden="true"
              className="tp-driver-empty-duck"
              draggable={false}
            />
            <button
              className="tp-button-primary tp-driver-empty-button"
              onClick={onCreate}
            >
              <CirclePlus className="h-5 w-5" aria-hidden="true" />
              <span>создать первые ссылки</span>
            </button>
          </section>
        </div>
      ) : null}

      {hasFlows ? (
        <ul className="tp-driver-flow-list">
          {items?.map((f) => {
            const normalizedStatus = normalizeStatus(f.status);
            const active = normalizedStatus === "active";
            const canDelete = active || normalizedStatus === "rejected";
            const deletePending = pendingDeleteId === f.id;
            return (
              <li key={f.id} className="tp-driver-flow-card">
                <div className="tp-driver-flow-head">
                  <div className="min-w-0">
                    <div className="tp-driver-flow-name">{f.name}</div>
                    <div className="tp-driver-flow-category">
                      {categoryLabel(f.resource_category)}
                    </div>
                  </div>
                  <span className="tp-chip">{statusLabel(f.status)}</span>
                </div>

                <div className="tp-driver-flow-link-row">
                  <div
                    className="tp-driver-flow-link"
                    title={active ? f.share_url : undefined}
                  >
                    {active ? f.share_url : "ссылка появится после модерации"}
                  </div>
                  <button
                    className="tp-driver-icon-button"
                    aria-label="скопировать ссылку"
                    disabled={!active}
                    onClick={() => copy(f)}
                  >
                    {copiedId === f.id ? (
                      <Check
                        className="h-[1.125rem] w-[1.125rem]"
                        aria-hidden="true"
                      />
                    ) : (
                      <Copy
                        className="h-[1.125rem] w-[1.125rem]"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                </div>

                <div
                  className="tp-driver-flow-today"
                  aria-label="сводка за сегодня"
                >
                  <div className="tp-driver-flow-stat">
                    <MousePointerClick
                      className="h-[1.125rem] w-[1.125rem]"
                      aria-hidden="true"
                    />
                    <span>запусков:</span>
                    <strong>{formatCount(f.today_clicks)}</strong>
                  </div>
                  <div className="tp-driver-flow-stat">
                    <UsersRound
                      className="h-[1.125rem] w-[1.125rem]"
                      aria-hidden="true"
                    />
                    <span>зачтено сегодня:</span>
                    <strong>{formatCount(f.today_subscribers)}</strong>
                  </div>
                </div>

                <div className="tp-driver-flow-actions">
                  <button
                    className="tp-button-secondary tp-driver-action-button"
                    disabled={!active}
                    onClick={() => onStats(f.id, f.name)}
                  >
                    <ChartNoAxesCombined
                      className="h-[1.125rem] w-[1.125rem]"
                      aria-hidden="true"
                    />
                    <span>статистика</span>
                  </button>
                  <button
                    className="tp-button-secondary tp-driver-action-button"
                    disabled={!active}
                    onClick={() => open(f.share_url)}
                  >
                    <ExternalLink
                      className="h-[1.125rem] w-[1.125rem]"
                      aria-hidden="true"
                    />
                    <span>открыть</span>
                  </button>
                  <button
                    disabled={!active}
                    onClick={() => onEdit(f.id)}
                    className="tp-button-secondary tp-driver-action-button"
                  >
                    <Settings
                      className="h-[1.125rem] w-[1.125rem]"
                      aria-hidden="true"
                    />
                    <span>настройки</span>
                  </button>
                  <button
                    disabled={!canDelete || deletingId === f.id}
                    onClick={() => requestRemove(f.id)}
                    className="tp-button-secondary tp-driver-action-button disabled:opacity-45"
                    aria-label={
                      deletePending
                        ? `подтвердить удаление ${f.name}`
                        : `удалить ${f.name}`
                    }
                    data-confirm={deletePending ? "true" : "false"}
                  >
                    <Trash2
                      className="h-[1.125rem] w-[1.125rem]"
                      aria-hidden="true"
                    />
                    <span>
                      {deletingId === f.id
                        ? "удаляем..."
                        : deletePending
                          ? "подтвердить"
                          : "удалить"}
                    </span>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

function formatCount(value: number | undefined): string {
  return new Intl.NumberFormat("ru-RU").format(value ?? 0);
}

function normalizeStatus(status: string): string {
  return status.toLowerCase();
}

function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    draft: "черновик",
    on_moderation: "на модерации",
    approved: "одобрен",
    active: "активен",
    paused: "пауза",
    finished: "завершён",
    rejected: "отклонен",
    disabled: "отключён",
  };
  return labels[normalizeStatus(status)] ?? status;
}

function categoryLabel(category: Flow["resource_category"]): string {
  if (category === "broadcast") return "рассылка";
  return "обязательная подписка";
}
